import React, { useState, useEffect, useCallback } from 'react';
import { accountingApi } from '../../../services/accountingApi';
import { Account, BudgetItem, BudgetVsActualReport, BudgetVsActualItem } from '../../../types/accounting';

interface BudgetManagerProps {
  businessId: number;
  formatCurrency: (amount: number) => string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type ViewMode = 'entry' | 'report';

const BudgetManager: React.FC<BudgetManagerProps> = ({ businessId, formatCurrency }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('report');
  const [year, setYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState<number | undefined>(undefined);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [budgetData, setBudgetData] = useState<Record<string, number>>({});
  const [report, setReport] = useState<BudgetVsActualReport | null>(null);
  const [reportPeriod, setReportPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [copyFromYear, setCopyFromYear] = useState(year - 1);
  const [copyAdjustment, setCopyAdjustment] = useState(0);
  const [showCopy, setShowCopy] = useState(false);

  // Load accounts and existing budgets
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, budgetsRes] = await Promise.all([
        accountingApi.getBusinessAccounts(businessId),
        accountingApi.getBudgets(businessId, year)
      ]);

      if (accountsRes.status === 'success') {
        const allAccounts: Account[] = [];
        const grouped = (accountsRes as any).accounts || {};
        Object.values(grouped).forEach((group: any) => {
          if (Array.isArray(group)) {
            group.forEach((acc: Account) => {
              if (acc.account_type === 'revenue' || acc.account_type === 'expense') {
                allAccounts.push(acc);
              }
            });
          }
        });
        allAccounts.sort((a, b) => a.account_number - b.account_number);
        setAccounts(allAccounts);
      }

      if (budgetsRes.status === 'success') {
        const data: Record<string, number> = {};
        const budgets = (budgetsRes as any).budgets || [];
        budgets.forEach((b: BudgetItem) => {
          const key = b.account_id + '-' + b.month;
          data[key] = b.amount;
        });
        setBudgetData(data);
      }
    } catch (err) {
      console.error('Failed to load budget data:', err);
    }
    setLoading(false);
  }, [businessId, year]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountingApi.getBudgetVsActual(businessId, year, reportMonth);
      if (res.status === 'success') {
        const response = res as any;
        setReport(response.budget_vs_actual);
        setReportPeriod(response.period || String(year));
      }
    } catch (err) {
      console.error('Failed to load budget vs actual:', err);
    }
    setLoading(false);
  }, [businessId, year, reportMonth]);

  useEffect(() => {
    if (viewMode === 'entry') {
      loadData();
    } else {
      loadReport();
    }
  }, [viewMode, loadData, loadReport]);

  const handleAmountChange = (accountId: number, month: number, value: string) => {
    const key = accountId + '-' + month;
    const num = parseFloat(value) || 0;
    setBudgetData(prev => ({ ...prev, [key]: num }));
  };

  const getAmount = (accountId: number, month: number): number => {
    return budgetData[accountId + '-' + month] || 0;
  };

  const getRowTotal = (accountId: number): number => {
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      total += getAmount(accountId, m);
    }
    return total;
  };

  const saveBudgets = async () => {
    setSaving(true);
    setMessage('');
    try {
      const items: { account_id: number; month: number; amount: number }[] = [];
      accounts.forEach(acc => {
        for (let m = 1; m <= 12; m++) {
          const amt = getAmount(acc.id, m);
          if (amt !== 0) {
            items.push({ account_id: acc.id, month: m, amount: amt });
          }
        }
      });

      const res = await accountingApi.saveBudgets(businessId, { year, items });
      if (res.status === 'success') {
        setMessage('Budget saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Failed to save budget');
    }
    setSaving(false);
  };

  const handleCopy = async () => {
    setSaving(true);
    try {
      const res = await accountingApi.copyBudget(businessId, copyFromYear, year, copyAdjustment);
      if (res.status === 'success') {
        setMessage('Budget copied! Reloading...');
        setShowCopy(false);
        await loadData();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Failed to copy budget');
    }
    setSaving(false);
  };

  const applyUniformAmount = (accountId: number) => {
    const janAmount = getAmount(accountId, 1);
    if (janAmount === 0) return;
    const newData = { ...budgetData };
    for (let m = 2; m <= 12; m++) {
      newData[accountId + '-' + m] = janAmount;
    }
    setBudgetData(newData);
  };

  const renderVarianceCell = (item: BudgetVsActualItem) => {
    const color = item.favorable ? '#059669' : '#dc2626';
    // A percentage of a zero budget means nothing
    const percent = item.budget === 0 ? '' : ` (${item.variance_pct}%)`;
    return (
      <td style={{ color, fontWeight: 500, textAlign: 'right' }}>
        {formatCurrency(item.variance)}{percent}
      </td>
    );
  };

  // Budget Entry View
  const renderEntryView = () => {
    const revenueAccounts = accounts.filter(a => a.account_type === 'revenue');
    const expenseAccounts = accounts.filter(a => a.account_type === 'expense');

    const renderAccountSection = (title: string, sectionAccounts: Account[]) => (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 8px', color: '#374151' }}>{title}</h4>
        {sectionAccounts.length === 0 ? (
          <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No {title.toLowerCase()} accounts</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #e5e7eb', minWidth: 160, position: 'sticky', left: 0, background: '#f9fafb' }}>Account</th>
                  {MONTHS.map(m => (
                    <th key={m} style={{ textAlign: 'right', padding: '6px 4px', borderBottom: '2px solid #e5e7eb', minWidth: 80 }}>{m}</th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '2px solid #e5e7eb', fontWeight: 700, minWidth: 90 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {sectionAccounts.map(acc => (
                  <tr key={acc.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '4px 8px', position: 'sticky', left: 0, background: '#fff', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#6b7280', marginRight: 4 }}>{acc.account_number}</span>
                      {acc.account_name}
                      <button
                        onClick={() => applyUniformAmount(acc.id)}
                        title="Copy Jan to all months"
                        style={{ marginLeft: 4, border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#9ca3af' }}
                      >
                        Fill
                      </button>
                    </td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <td key={m} style={{ padding: '2px 2px' }}>
                        <input
                          type="number"
                          value={getAmount(acc.id, m) || ''}
                          onChange={e => handleAmountChange(acc.id, m, e.target.value)}
                          style={{
                            width: '100%', textAlign: 'right', padding: '4px',
                            border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.8rem',
                            background: getAmount(acc.id, m) ? '#fff' : '#fafafa'
                          }}
                          placeholder="0"
                        />
                      </td>
                    ))}
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(getRowTotal(acc.id))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ fontWeight: 500 }}>Budget Year:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}>
            {[year - 1, year, year + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={() => setShowCopy(!showCopy)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer' }}>
            Copy from Previous Year
          </button>
          <button onClick={saveBudgets} disabled={saving}
            style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
            {saving ? 'Saving...' : 'Save Budget'}
          </button>
          {message && <span style={{ color: message.includes('Failed') ? '#dc2626' : '#059669', fontWeight: 500 }}>{message}</span>}
        </div>

        {showCopy && (
          <div style={{ background: '#f0f9ff', padding: 12, borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Copy from</span>
            <select value={copyFromYear} onChange={e => setCopyFromYear(Number(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}>
              {[year - 2, year - 1, year].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <span>to {year} with</span>
            <input type="number" value={copyAdjustment} onChange={e => setCopyAdjustment(Number(e.target.value))}
              style={{ width: 60, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', textAlign: 'right' }}
            />
            <span>% adjustment</span>
            <button onClick={handleCopy} disabled={saving}
              style={{ padding: '4px 12px', borderRadius: 4, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>
              Copy
            </button>
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            {renderAccountSection('Revenue', revenueAccounts)}
            {renderAccountSection('Expenses', expenseAccounts)}
          </>
        )}
      </div>
    );
  };

  // Budget vs Actual Report View
  const renderReportView = () => {
    if (loading) return <p>Loading report...</p>;
    if (!report) return <p style={{ color: '#9ca3af' }}>No budget data for this period. Add figures in the “Set Budgets” tab first.</p>;

    const { totals } = report;

    const renderSection = (title: string, items: BudgetVsActualItem[], budgetTotal: number, actualTotal: number, varianceTotal: number) => (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 8px', color: '#374151' }}>{title}</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e5e7eb' }}>Account</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '2px solid #e5e7eb' }}>Budget</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '2px solid #e5e7eb' }}>Actual</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '2px solid #e5e7eb' }}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.account_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '6px 12px' }}>
                  <span style={{ color: '#6b7280', marginRight: 4 }}>{item.account_number}</span>
                  {item.account_name}
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>{formatCurrency(item.budget)}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>{formatCurrency(item.actual)}</td>
                {renderVarianceCell(item)}
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: '2px solid #e5e7eb', background: '#f9fafb' }}>
              <td style={{ padding: '8px 12px' }}>Total {title}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(budgetTotal)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(actualTotal)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', color: varianceTotal >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                {formatCurrency(varianceTotal)} ({budgetTotal !== 0 ? ((varianceTotal / budgetTotal) * 100).toFixed(1) : 0}%)
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ fontWeight: 500 }}>Year:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}>
            {[year - 1, year, year + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <label style={{ fontWeight: 500, marginLeft: 8 }}>Month:</label>
          <select value={reportMonth || ''} onChange={e => setReportMonth(e.target.value ? Number(e.target.value) : undefined)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}>
            <option value="">Full Year</option>
            {MONTHS.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <span style={{ color: '#6b7280' }}>Period: {reportPeriod}</span>
        </div>

        {renderSection('Revenue', report.revenue, totals.revenue_budget, totals.revenue_actual, totals.revenue_variance)}
        {renderSection('Expenses', report.expenses, totals.expense_budget, totals.expense_actual, totals.expense_variance)}

        {/* Net Income Summary */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16, marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
            <tbody>
              <tr style={{ fontWeight: 700 }}>
                <td style={{ padding: '4px 12px' }}>Net Income</td>
                <td style={{ padding: '4px 12px', textAlign: 'right' }}>{formatCurrency(totals.net_income_budget)}</td>
                <td style={{ padding: '4px 12px', textAlign: 'right' }}>{formatCurrency(totals.net_income_actual)}</td>
                <td style={{ padding: '4px 12px', textAlign: 'right', color: totals.net_income_variance >= 0 ? '#059669' : '#dc2626' }}>
                  {formatCurrency(totals.net_income_variance)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setViewMode('report')}
          style={{
            padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 500,
            background: viewMode === 'report' ? '#2563eb' : '#f3f4f6',
            color: viewMode === 'report' ? '#fff' : '#374151'
          }}>
          Budget vs Actual Report
        </button>
        <button
          onClick={() => setViewMode('entry')}
          style={{
            padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 500,
            background: viewMode === 'entry' ? '#2563eb' : '#f3f4f6',
            color: viewMode === 'entry' ? '#fff' : '#374151'
          }}>
          Set Budgets
        </button>
      </div>

      {viewMode === 'entry' ? renderEntryView() : renderReportView()}
    </div>
  );
};

export default BudgetManager;
